import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "maplibre-gl/dist/maplibre-gl.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ResourceRollupTable } from "./ResourceTables";
import { getCommodityMeta } from "./functions";
import ResourcesProductionInfoDialog from "./ResourcesProductionInfoDialog";
import { formatJurisdictionLabel, formatPercent } from "../../utils/companiesFormatting";
import { getUnitFromMetric, resolveResourceUnit } from "../../utils/resourceFormatting";
import AssetResourceDetailDrawer from "./AssetResourceDetailDrawer";
import { formatAsOfDate, getResourceAsOfDate, getResourceLastUpdatedDate } from "./assetResourceDetailUtils";
import { TableHeaderRow, TableCell } from "../ui/TableShell";

const FOOTPRINT_MAP_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "(c) OpenStreetMap contributors",
    },
  },
  layers: [
    {
      id: "osm",
      type: "raster",
      source: "osm",
    },
  ],
};

const LIFECYCLE_COLORS = {
  production: "#16a34a",
  development: "#eab308",
  exploration: "#2563eb",
};

const FOOTPRINT_LEGEND = [
  { key: "production", label: "Production", color: LIFECYCLE_COLORS.production },
  { key: "development", label: "Development", color: LIFECYCLE_COLORS.development },
  { key: "exploration", label: "Exploration", color: LIFECYCLE_COLORS.exploration },
];

const getAssetCoordinates = (asset) => {
  if (
    asset?.latitude === null ||
    asset?.latitude === undefined ||
    asset?.latitude === "" ||
    asset?.longitude === null ||
    asset?.longitude === undefined ||
    asset?.longitude === ""
  ) {
    return null;
  }
  const latitude = Number(asset?.latitude);
  const longitude = Number(asset?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude, longitude };
};

const getLifecycleKey = (asset) => {
  const raw = `${asset?.lifecycle_code || asset?.lifecycle || ""}`.toLowerCase();
  if (raw.includes("production")) return "production";
  if (raw.includes("development")) return "development";
  if (raw.includes("exploration")) return "exploration";
  return null;
};

const getLifecycleLabel = (asset, lifecycleKey) => {
  if (asset?.lifecycle) return asset.lifecycle;
  const legendItem = FOOTPRINT_LEGEND.find((item) => item.key === lifecycleKey);
  return legendItem?.label || "Lifecycle";
};

const createCircleMarkerElement = (color, label) => {
  const element = document.createElement("button");
  element.type = "button";
  element.setAttribute("aria-label", label || "Asset location");
  Object.assign(element.style, {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: "2px solid #ffffff",
    backgroundColor: color,
    boxShadow: "0 1px 4px rgba(15, 23, 42, 0.45)",
    cursor: "pointer",
    padding: "0",
  });
  return element;
};

const createAssetPopupContent = (name, lifecycle) => {
  const container = document.createElement("div");
  const title = document.createElement("div");
  const detail = document.createElement("div");
  title.textContent = name || "Asset";
  detail.textContent = lifecycle || "Lifecycle";
  title.style.fontWeight = "700";
  detail.style.marginTop = "2px";
  detail.style.color = "#475569";
  container.append(title, detail);
  return container;
};

function GeographicFootprintMap({ assets, onAssetClick }) {
  const mapContainerRef = useRef(null);
  const mappedAssets = useMemo(
    () =>
      (assets || [])
        .map((asset) => {
          const coordinates = getAssetCoordinates(asset);
          const lifecycleKey = getLifecycleKey(asset);
          if (!coordinates || !lifecycleKey) return null;
          return {
            asset,
            coordinates,
            color: LIFECYCLE_COLORS[lifecycleKey],
            lifecycle: getLifecycleLabel(asset, lifecycleKey),
          };
        })
        .filter(Boolean),
    [assets]
  );

  useEffect(() => {
    if (!mapContainerRef.current || mappedAssets.length === 0) return undefined;
    let map = null;
    let markers = [];
    let isMounted = true;

    import("maplibre-gl").then(
      ({ AttributionControl, LngLatBounds, Map: MapLibreMap, Marker, NavigationControl, Popup, ScaleControl }) => {
        if (!isMounted || !mapContainerRef.current) return;
        const first = mappedAssets[0].coordinates;
        map = new MapLibreMap({
          container: mapContainerRef.current,
          style: FOOTPRINT_MAP_STYLE,
          center: [first.longitude, first.latitude],
          zoom: 3,
          minZoom: 1,
          maxZoom: 14,
          attributionControl: false,
          scrollZoom: false,
          dragPan: true,
          pitchWithRotate: false,
          dragRotate: false,
        });

        map.addControl(
          new NavigationControl({
            showCompass: false,
            visualizePitch: false,
          }),
          "top-right"
        );
        map.addControl(new ScaleControl({ maxWidth: 100, unit: "metric" }), "bottom-left");
        map.addControl(new AttributionControl({ compact: true }), "bottom-right");

        const bounds = new LngLatBounds();
        markers = mappedAssets.map(({ asset, coordinates, color, lifecycle }) => {
          bounds.extend([coordinates.longitude, coordinates.latitude]);
          const label = asset.name || asset.asset_name;
          const markerElement = createCircleMarkerElement(color, label);
          const popup = new Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 14,
          }).setDOMContent(createAssetPopupContent(label, lifecycle));
          markerElement.addEventListener("mouseenter", () => {
            popup.setLngLat([coordinates.longitude, coordinates.latitude]).addTo(map);
          });
          markerElement.addEventListener("mouseleave", () => {
            popup.remove();
          });
          markerElement.addEventListener("click", () => {
            popup.remove();
            if (onAssetClick) onAssetClick(asset);
          });
          const marker = new Marker({ element: markerElement })
            .setLngLat([coordinates.longitude, coordinates.latitude]);
          marker.addTo(map);
          return marker;
        });

        if (mappedAssets.length > 1) {
          map.fitBounds(bounds, { padding: 44, maxZoom: 6, duration: 0 });
        } else {
          map.setZoom(6);
        }
      }
    );

    return () => {
      isMounted = false;
      markers.forEach((marker) => {
        marker.getElement?.().dispatchEvent(new Event("mouseleave"));
        marker.remove();
      });
      map?.remove();
    };
  }, [mappedAssets, onAssetClick]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          position: "relative",
          height: 350,
          width: "100%",
          borderRadius: 1.5,
          border: "1px solid rgba(0,0,0,0.08)",
          overflow: "hidden",
          backgroundColor: "rgba(15, 23, 42, 0.04)",
        }}
      >
        {mappedAssets.length ? (
          <Box
            ref={mapContainerRef}
            sx={{
              height: "100%",
              width: "100%",
              "& .maplibregl-ctrl-group": {
                boxShadow: "0 1px 4px rgba(15, 23, 42, 0.18)",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No asset coordinates available.
            </Typography>
          </Box>
        )}
        <Stack
          spacing={0.75}
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 1,
            backgroundColor: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(15, 23, 42, 0.12)",
            borderRadius: 1,
            px: 1,
            py: 0.75,
            boxShadow: "0 1px 4px rgba(15, 23, 42, 0.12)",
          }}
        >
          {FOOTPRINT_LEGEND.map((item) => (
            <Stack key={item.key} direction="row" spacing={0.75} alignItems="center">
              <Box
                component="span"
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: item.color,
                  border: "1px solid rgba(15, 23, 42, 0.25)",
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

const formatSummaryValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: Number.isInteger(parsed) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(parsed);
};

const formatDiscountRate = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return `${formatSummaryValue(parsed)}%`;
};

const formatIrrValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return `${formatSummaryValue(parsed)}%`;
};

const formatPaybackPeriod = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;
  return `${formatSummaryValue(parsed)} yrs`;
};

const formatPriceAssumptions = (prices = []) => {
  if (!prices.length) return "—";
  return prices
    .map((price) => {
      const label = price.commodity || "Commodity";
      const unit = price.unit ? `/${price.unit}` : "";
      const val = price.price !== null && price.price !== undefined ? formatSummaryValue(price.price) : "—";
      return `${label} ${val}${unit}`;
    })
    .join(", ");
};

const formatTaxTreatment = (value) => {
  if (!value) return "—";
  const normalized = String(value).toLowerCase();
  if (normalized === "pre_tax" || normalized === "pre-tax") return "Pre-tax";
  if (normalized === "after_tax" || normalized === "after-tax") return "After-tax";
  return value;
};


export default function AssetsTab({ data = null, loading = false, error = null }) {
  const summaryRows = data?.company_summary?.resource_estimates || [];
  const assets = data?.assets || [];
  const companyAsOfDate = (() => {
    let latest = null;
    (assets || []).forEach((asset) => {
      (asset.resources || []).forEach((resource) => {
        const dateValue = getResourceLastUpdatedDate(resource);
        if (!dateValue) return;
        const parsed = new Date(dateValue);
        if (Number.isNaN(parsed.getTime())) return;
        if (!latest || parsed > latest) {
          latest = parsed;
        }
      });
    });
    return latest ? formatAsOfDate(latest.toISOString()) : "—";
  })();
  const classificationOptions = [
    { value: "proven", label: "Proven" },
    { value: "probable", label: "Probable" },
    { value: "measured", label: "Measured" },
    { value: "indicated", label: "Indicated" },
    { value: "inferred", label: "Inferred" },
  ];
  const [infoOpen, setInfoOpen] = useState(false);
  const [expandedAssets, setExpandedAssets] = useState({});
  const [detailDrawer, setDetailDrawer] = useState({ open: false, type: "", data: null });

  const allExpanded = assets.length > 0 && assets.every((asset) => expandedAssets[asset.id]);

  const toggleAssetExpanded = (assetId) => {
    setExpandedAssets((prev) => ({
      ...prev,
      [assetId]: !prev[assetId],
    }));
  };

  const expandAllAssets = () => {
    const next = {};
    assets.forEach((asset) => {
      next[asset.id] = true;
    });
    setExpandedAssets(next);
  };

  const collapseAllAssets = () => {
    setExpandedAssets({});
  };
  const openDetailDrawer = (type, data) => {
    setDetailDrawer({ open: true, type, data });
  };
  const openAssetDetailDrawer = useCallback((asset) => {
    setDetailDrawer({ open: true, type: "asset", data: asset });
  }, []);
  const closeDetailDrawer = () => {
    setDetailDrawer({ open: false, type: "", data: null });
  };


  const buildSummaryForTier = (tier) =>
    summaryRows
      .map((row) => {
        const proven = Number(row.proven || 0);
        const probable = Number(row.probable || 0);
        const measured = Number(row.measured || 0);
        const indicated = Number(row.indicated || 0);
        const inferred = Number(row.inferred || 0);
        let total = 0;
        if (tier === "proven_probable") {
          total = proven + probable;
        } else if (tier === "measured_indicated") {
          total = measured + indicated;
        } else if (tier === "inferred") {
          total = inferred;
        }
        const unit = row.unit || getUnitFromMetric(row.metric, "—");
        return {
          commodity: row.commodity,
          unit,
          total,
        };
      })
      .filter((row) => row.commodity);

  const hasTotals = (rows) => rows.some((row) => row.total > 0);

  const provenProbableSummary = buildSummaryForTier("proven_probable");
  const measuredIndicatedSummary = buildSummaryForTier("measured_indicated");
  const inferredSummary = buildSummaryForTier("inferred");
  const summaryTier = hasTotals(provenProbableSummary)
    ? "proven_probable"
    : hasTotals(measuredIndicatedSummary)
      ? "measured_indicated"
      : hasTotals(inferredSummary)
        ? "inferred"
        : "none";
  const summaryLabel =
    summaryTier === "proven_probable"
      ? "Proven & Probable Contained Metal"
      : summaryTier === "measured_indicated"
        ? "Measured + Indicated Contained Metal"
        : summaryTier === "inferred"
          ? "Inferred Contained Metal"
          : "No Resources & Reserves";
  const summaryRowsToRender =
    summaryTier === "proven_probable"
      ? provenProbableSummary
      : summaryTier === "measured_indicated"
        ? measuredIndicatedSummary
        : summaryTier === "inferred"
          ? inferredSummary
          : [];

  const hasMeaningfulValue = (value) => {
    if (value === null || value === undefined) return false;
    const normalized = `${value}`.trim();
    if (!normalized) return false;
    const numeric = Number(normalized);
    if (!Number.isNaN(numeric)) {
      return numeric !== 0;
    }
    return true;
  };

  const buildCommodityCatalog = () => {
    const order = [];
    const metaByKey = new Map();
    (assets || []).forEach((asset) => {
      (asset.resources || []).forEach((resource) => {
        const values = resource.latest_resource_report?.values || [];
        values.forEach((val) => {
          const commodityKey =
            val.commodity?.id || val.commodity || val.commodity?.name || "Commodity";
          if (metaByKey.has(commodityKey)) return;
          const commodityName = val.commodity?.name || val.commodity || "Commodity";
          metaByKey.set(commodityKey, {
            key: commodityKey,
            name: commodityName,
            gradeUnit: resolveResourceUnit(
              val.grade_unit || val.commodity?.grade_unit,
              commodityName,
              "gradeUnit"
            ),
            containedUnit: resolveResourceUnit(
              val.contained_unit || val.commodity?.contained_unit,
              commodityName,
              "containedUnit"
            ),
          });
          order.push(commodityKey);
        });
      });
    });
    return order.map((key) => metaByKey.get(key));
  };

  const aggregateValuesByClassification = (values) => {
    const rows = {};
    const tonnageSeen = new Set();
    values.forEach((val) => {
      const classification = val.classification_level;
      if (!classification) return;
      const commodityKey =
        val.commodity?.id || val.commodity || val.commodity?.name || "Commodity";
      const tonnage = Number(val.tonnage_mt);
      const grade = Number(val.grade_value);
      const contained = Number(val.contained_value);
      const hasTonnage = hasMeaningfulValue(tonnage);
      const hasGrade = hasMeaningfulValue(grade);
      const hasContained = hasMeaningfulValue(contained);
      if (!rows[classification]) {
        rows[classification] = {
          tonnageSum: 0,
          gradeWeightedSum: {},
          gradeWeight: {},
          containedSum: {},
        };
      }
      const tonnageKey = `${val.report_id || "unknown"}:${classification}`;
      if (!Number.isNaN(tonnage) && hasTonnage && !tonnageSeen.has(tonnageKey)) {
        rows[classification].tonnageSum += tonnage;
        tonnageSeen.add(tonnageKey);
      }
      if (!Number.isNaN(grade) && hasGrade) {
        const weight = !Number.isNaN(tonnage) && tonnage > 0 ? tonnage : 1;
        rows[classification].gradeWeightedSum[commodityKey] =
          (rows[classification].gradeWeightedSum[commodityKey] || 0) + grade * weight;
        rows[classification].gradeWeight[commodityKey] =
          (rows[classification].gradeWeight[commodityKey] || 0) + weight;
      }
      if (!Number.isNaN(contained) && hasContained) {
        rows[classification].containedSum[commodityKey] =
          (rows[classification].containedSum[commodityKey] || 0) + contained;
      }
    });
    return rows;
  };

  const buildRowsForValues = (values, commodities) => {
    const aggregated = aggregateValuesByClassification(values || []);
    return classificationOptions
      .map((opt) => {
        const row = aggregated[opt.value];
        if (!row) return null;
        const grades = {};
        const contained = {};
        (commodities || []).forEach((commodity) => {
          const key = commodity?.key;
          if (!key) return;
          const gradeWeight = row.gradeWeight?.[key] || 0;
          grades[key] = gradeWeight
            ? (row.gradeWeightedSum?.[key] || 0) / gradeWeight
            : null;
          contained[key] = row.containedSum?.[key] ?? null;
        });
        const hasAnyValue =
          hasMeaningfulValue(row.tonnageSum) ||
          (commodities || []).some((commodity) => hasMeaningfulValue(grades[commodity.key])) ||
          (commodities || []).some((commodity) => hasMeaningfulValue(contained[commodity.key]));
        if (!hasAnyValue) return null;
        return {
          classification: opt,
          tonnage: row.tonnageSum,
          grades,
          contained,
        };
      })
      .filter(Boolean);
  };

  const allCommodities = buildCommodityCatalog();
  const commodityColumnCount = allCommodities.length || 1;
  const detailColumnCount = 3 + commodityColumnCount * 2;

  const companyValues = assets.flatMap((asset) =>
    (asset.resources || []).flatMap((resource) => resource.latest_resource_report?.values || [])
  );
  const companyRows = buildRowsForValues(companyValues, allCommodities);

  const uniqueCountries = new Set(
    assets
      .map((asset) => {
        const jurisdiction = asset?.jurisdiction;
        if (!jurisdiction) return null;
        if (typeof jurisdiction === "string") {
          return jurisdiction.split(" - ")[0].trim();
        }
        return jurisdiction.country || null;
      })
      .filter(Boolean)
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, height: "100%", maxWidth: {xs: 350, sm: 1500} }}>
      <Stack
        direction="row"
        spacing={{ xs: 0.5, sm: 1 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ pb: { xs: 0.5, sm: 1 } }}
      >
        <Typography variant="h6" fontWeight={700}>
          Assets
        </Typography>
        {!loading && !error && assets.length > 0 && (
          <IconButton
            size="small"
            onClick={() => setInfoOpen(true)}
            aria-label="Assets information"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <ResourcesProductionInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
      {loading ? (
        <Box sx={{ mt: 2 }}>
          <Skeleton height={40} />
          <Skeleton height={160} sx={{ mt: 1 }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : assets.length === 0 ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No published asset information available.
          </Typography>
          <Divider  sx={{pt:2, mb:2}} />
          <Typography variant="body2" color="text.secondary" sx={{pt:2}}>
            <b>ResourceNAV</b> is currently working with companies to build out this asset database to
            create a rich dataset of resources, production and forecast metrics to use for enhanced display & screening
            purposes.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you are a mining company, claim your company profile and enter in asset
            information to publish data on the platform.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1.5,
              backgroundColor: "rgba(34, 35, 37, 0.02)",
              boxShadow: "0 1px 6px rgba(15, 23, 42, 0.06)",
              px: 2.5,
              py: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 2,
                alignItems: "center",
              }}
            >
              <Box sx={{ pr: { md: 2 } }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {summaryLabel}
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {summaryRowsToRender.length ? (
                    summaryRowsToRender.map((row) => {
                      const meta = getCommodityMeta(row.commodity);
                      const label = meta?.label || row.commodity;
                      const chipLabel = meta?.symbol || row.commodity;
                      const value = row.total > 0 ? formatSummaryValue(row.total) : "—";
                      return (
                        <Stack
                          key={`${row.commodity}-${row.unit}`}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Chip
                            label={chipLabel}
                            size="small"
                            sx={{
                              backgroundColor: meta?.color || "action.hover",
                              color: "#0f172a",
                              fontWeight: 700,
                              height: 28,
                            }}
                            title={label}
                          />
                          <Typography variant="subtitle2" fontWeight={700}>
                            {label}
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                            {value} {row.unit}
                          </Typography>
                        </Stack>
                      );
                    })
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No Resources & Reserves.
                    </Typography>
                  )}
                </Stack>
              </Box>
              <Box
                sx={{
                  borderLeft: { md: "1px solid rgba(0,0,0,0.08)" },
                  pl: { md: 2 },
                  py: { xs: 1, md: 0 },
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Assets
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {assets.length}
                </Typography>
              </Box>
              <Box
                sx={{
                  borderLeft: { md: "1px solid rgba(0,0,0,0.08)" },
                  pl: { md: 2 },
                  py: { xs: 1, md: 0 },
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Jurisdictions (Countries)
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {uniqueCountries.size}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Contained Metal Summary
            </Typography>
            <ResourceRollupTable
              rows={summaryRows}
              emptyLabel="No published estimates available."
            />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Asset Portfolio
            </Typography>
            <GeographicFootprintMap
              assets={assets}
              onAssetClick={openAssetDetailDrawer}
            />
          </Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Asset Details
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, '&': { marginTop: '0px !important' } }}> 
            <Typography variant="caption" color="text.secondary">
              <i>Resources last updated: {companyAsOfDate}</i>
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={allExpanded ? collapseAllAssets : expandAllAssets}
              sx={{ fontWeight: 600 }}
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </Button>
          </Stack>
          <Box
            sx={{
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1.5,
              overflowX: { xs: "scroll", md: "auto" },
              WebkitOverflowScrolling: "touch",
              contain: "inline-size",
            }}
          >
            <Box
              component="table"
              sx={{
                width: "100%",
                borderCollapse: "collapse"
              }}
            >
              <Box component="thead">
                <TableHeaderRow component="tr">
                    <TableCell
                      component="th"
                      sx={{
                        px: 0,
                        py: 0.5,
                        textAlign: "left",
                      width: { xs: 125, sm: 300 },
                      minWidth: { xs: 125, sm: 300 },
                      maxWidth: { xs: 125, sm: 300 },
                        borderRight: "1px solid rgba(0,0,0,0.08)",
                      }}
                    />
                  <TableCell
                    component="th"
                    align="center"
                    sx={{ px: 0.5, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)" }}
                    rowSpan={2}
                  >
                    <Typography variant="caption" fontWeight={700}>
                      Classification
                    </Typography>
                  </TableCell>
                  <TableCell
                    component="th"
                    align="center"
                    sx={{ px: 0.5, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)" }}
                    rowSpan={2}
                  >
                    <Typography variant="caption" fontWeight={700}>
                      Tonnage
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", fontSize: "0.6875rem" }}>
                      (Mt)
                    </Typography>
                  </TableCell>
                  <TableCell
                    component="th"
                    align="center"
                    sx={{ px: 0, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)" }}
                    colSpan={(allCommodities.length || 1)}
                  >
                    <Typography variant="caption" fontWeight={700}>
                      Grade
                    </Typography>
                  </TableCell>
                  <TableCell
                    component="th"
                    align="center"
                    sx={{ px: 0, py: 0.5 }}
                    colSpan={(allCommodities.length || 1)}
                  >
                    <Typography variant="caption" fontWeight={700}>
                      Contained Metal
                    </Typography>
                  </TableCell>
                </TableHeaderRow>
                <TableHeaderRow component="tr">
                  <TableCell
                    component="th"
                    sx={{
                      px: 0.5,
                      py: 0.5,
                      width: { xs: 125, sm: 300 },
                      minWidth: { xs: 125, sm: 300 },
                      maxWidth: { xs: 125, sm: 300 },
                      borderRight: "1px solid rgba(0,0,0,0.08)",
                    }}
                  />
                  {(allCommodities.length ? allCommodities : [null]).map((commodity, commodityIndex) => {
                    if (!commodity) {
                      return (
                        <Box component="th" key="grade-empty" sx={{ px: 0.5, py: 0.5 }} />
                      );
                    }
                    const meta = getCommodityMeta(commodity?.name || commodity?.key);
                    const label = meta?.label || commodity?.name || "Commodity";
                    return (
                      <Box
                        component="th"
                        key={`grade-${commodity.key}`}
                        sx={{
                          px: 0.25,
                          pb: 0.5,
                          textAlign: "center",
                          borderRight: commodityIndex === (allCommodities.length || 1) - 1 ? "1px solid rgba(0,0,0,0.08)" : "none",
                        }}
                      >
                        <Stack direction="row" spacing={0.125} justifyContent="center" alignItems="center">
                          <Chip
                            label={meta?.symbol || label}
                            size="small"
                            sx={{
                              backgroundColor: meta?.color || "action.hover",
                              color: "#0f172a",
                              fontWeight: 700,
                              height: 20,
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize:'0.6875rem' }}>
                            ({commodity.gradeUnit})
                          </Typography>
                        </Stack>
                      </Box>
                    );
                  })}
                  {(allCommodities.length ? allCommodities : [null]).map((commodity) => {
                    if (!commodity) {
                      return (
                        <Box component="th" key="contained-empty" sx={{ px: 0.5, py: 0.5 }} />
                      );
                    }
                    const meta = getCommodityMeta(commodity?.name || commodity?.key);
                    const label = meta?.label || commodity?.name || "Commodity";
                    return (
                      <Box
                        component="th"
                        key={`contained-${commodity.key}`}
                        sx={{
                          px: 0.25,
                          pb: 0.5,
                          textAlign: "center",
                        }}
                      >
                        <Stack direction="row" spacing={0.125} justifyContent="center" alignItems="center">
                          <Chip
                            label={meta?.symbol || label}
                            size="small"
                            sx={{
                              backgroundColor: meta?.color || "action.hover",
                              color: "#0f172a",
                              fontWeight: 700,
                              height: 20,
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize:'0.6875rem' }}>
                            ({commodity.containedUnit})
                          </Typography>
                        </Stack>
                      </Box>
                    );
                  })}
                </TableHeaderRow>
              </Box>
              <Box component="tbody">
                {assets.map((asset) => {
                  const isExpanded = Boolean(expandedAssets[asset.id]);
                  const assetValues = (asset.resources || []).flatMap(
                    (resource) => resource.latest_resource_report?.values || []
                  );
                  const assetRows = buildRowsForValues(assetValues, allCommodities);
                  const assetRowLabel = (
                    <Stack direction="column" spacing={0.25} alignItems="flex-start">
                      <Stack direction="row" spacing={0} alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => toggleAssetExpanded(asset.id)}
                          sx={{ mt: 0, width: 24, height: 24 }}
                          aria-label="Toggle asset resources"
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 20,
                              height: 20,
                              fontSize: "0.75rem",
                              lineHeight: 1,
                              color: "text.secondary",
                            }}
                          >
                            {isExpanded ? "▼" : "▶"}
                          </Box>
                        </IconButton>
                        <Tooltip title={asset.name || ""} placement="top" arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              cursor: "pointer",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                              maxWidth: { xs: 125, sm: 240 },
                              whiteSpace: { xs: "normal", sm: "nowrap" },
                              overflow: { xs: "visible", sm: "hidden" },
                              textOverflow: { xs: "clip", sm: "ellipsis" },
                              display: "block",
                            }}
                            onClick={() => openDetailDrawer("asset", asset)}
                          >
                            {asset.name}
                          </Typography>
                        </Tooltip>
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem", pl: 3 }}
                      >
                        {(() => {
                          const parts = [
                            formatJurisdictionLabel(asset.jurisdiction),
                            asset.lifecycle || "\u2014",
                            formatPercent(asset.ownership_percentage, 2) || "\u2014",
                          ].filter(Boolean);
                          return parts.length ? parts.join(" · ") : "\u2014";
                        })()}
                      </Typography>
                    </Stack>
                  );

                  const assetRowsRendered = (assetRows.length ? assetRows : [null]).map(
                    (row, index) => (
                      <Box
                        component="tr"
                        key={`asset-row-${asset.id}-${index}`}
                        sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", backgroundColor: "rgba(34, 35, 37, 0.02)", boxShadow: "inset 4px 0 0 rgba(15, 23, 42, 0.45)" }}
                      >
                        <Box
                          component="td"
                          sx={{
                            px: 0.5,
                            py: 0.5,
                            verticalAlign: "top",
                            width: { xs: 125, sm: 300 },
                            minWidth: { xs: 125, sm: 300 },
                            maxWidth: { xs: 125, sm: 300 },
                            borderRight: "1px solid rgba(0,0,0,0.08)",
                          }}
                        >
                          {index === 0 ? assetRowLabel : null}
                        </Box>
                        <Box component="td" sx={{ px: 0, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)",  textAlign: "center" }}>
                          {row ? (
                            <Typography variant="caption" fontWeight={600}>
                              {row.classification.label}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.75rem" }}> — </Typography>
                          )}
                        </Box>
                        <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center", borderRight: "1px solid rgba(0,0,0,0.08)" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row ? "text.primary" : "text.disabled" }}>{row ? formatSummaryValue(row.tonnage) : "—"}</Typography>
                        </Box>
                        {(allCommodities.length ? allCommodities : [null]).map(
                          (commodity, commodityIndex) => (
                            <Box
                              component="td"
                              key={`asset-grade-${asset.id}-${index}-${commodity?.key || "empty"}`}
                              sx={{
                                px: 0.5,
                                py: 0.5,
                                textAlign: "center",
                                borderRight: commodityIndex === (allCommodities.length || 1) - 1 ? "1px solid rgba(0,0,0,0.08)" : "none",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row && commodity ? "text.primary" : "text.disabled" }}>
                                {row && commodity
                                  ? formatSummaryValue(row.grades?.[commodity.key])
                                  : "—"}
                              </Typography>
                            </Box>
                          )
                        )}
                        {(allCommodities.length ? allCommodities : [null]).map(
                          (commodity, commodityIndex) => (
                            <Box
                              component="td"
                              key={`asset-contained-${asset.id}-${index}-${commodity?.key || "empty"}`}
                              sx={{
                                px: 0.5,
                                py: 0.5,
                                textAlign: "center",
                                borderRight: commodityIndex === (allCommodities.length || 1) - 1 ? "1px solid rgba(0,0,0,0.08)" : "none",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row && commodity ? "text.primary" : "text.disabled" }}>
                                {row && commodity
                                  ? formatSummaryValue(row.contained?.[commodity.key])
                                  :"—"}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Box>
                    )
                  );

                  if (!isExpanded) {
                    return assetRowsRendered;
                  }

                  const resourceRowsRendered = (asset.resources || []).flatMap((resource) => {
                    const resourceValues = resource.latest_resource_report?.values || [];
                    const resourceRows = buildRowsForValues(resourceValues, allCommodities);
                    const resourceLabel = (
                      <Box>
                        <Stack direction="column" spacing={0.25} alignItems="flex-start">
                          <Tooltip title={resource.name || "Resources & Reserves"} placement="top" arrow>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color="text.secondary"
                              sx={{
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                textDecoration: "none",
                                "&:hover": { textDecoration: "underline" },
                                maxWidth: { xs: 125, sm: 240 },
                                whiteSpace: { xs: "normal", sm: "nowrap" },
                                overflow: { xs: "visible", sm: "hidden" },
                                textOverflow: { xs: "clip", sm: "ellipsis" },
                                display: "block",
                                pl: 4,
                              }}
                              onClick={() => openDetailDrawer("resource", resource)}
                            >
                              {resource.name || "Resources & Reserves"}
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem", pl: 4 }}
                          >
                            {(() => {
                              const reportDate = getResourceAsOfDate(resource);
                              const formattedDate = reportDate ? "Last updated: " + formatAsOfDate(reportDate) : null;
                              const parts = [
                                resource.lifecycle || resource.lifecycle_stage || "—",
                                formattedDate,
                              ].filter(Boolean);
                              return parts.length ? parts.join(" · ") : "\u2014"
                            })()}
                          </Typography>
                        </Stack>
                      </Box>
                    );

                    const resourceRowEntries = (resourceRows.length ? resourceRows : [null]).map((row, index) => (
                      <Box
                        component="tr"
                        key={`resource-row-${resource.id}-${index}`}
                        sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", boxShadow: "inset 4px 0 0 rgba(15, 23, 42, 0.25)" }}
                      >
                        <Box
                          component="td"
                          sx={{
                            px: 0.5,
                            py: 0.5,
                            verticalAlign: "top",
                            width: { xs: 125, sm: 300 },
                            minWidth: { xs: 125, sm: 300 },
                            maxWidth: { xs: 125, sm: 300 },
                            borderRight: "1px solid rgba(0,0,0,0.08)",
                          }}
                        >
                          {index === 0 ? resourceLabel : null}
                        </Box>
                        <Box component="td" sx={{ px: 0.5, py: 0.5, borderRight: "1px solid rgba(0,0,0,0.08)",  textAlign: "center" }}>
                          {row ? (
                            <Typography variant="caption" fontWeight={600}>
                              {row.classification.label}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.75rem" }}> — </Typography>
                          )}
                        </Box>
                        <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center", borderRight: "1px solid rgba(0,0,0,0.08)" }}>
                          <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row ? "text.primary" : "text.disabled" }}>{row ? formatSummaryValue(row.tonnage) : "—"}</Typography>
                        </Box>
                        {(allCommodities.length ? allCommodities : [null]).map(
                          (commodity, commodityIndex) => (
                            <Box
                              component="td"
                              key={`resource-grade-${resource.id}-${index}-${commodity?.key || "empty"}`}
                              sx={{
                                px: 0.5,
                                py: 0.5,
                                textAlign: "center",
                                borderRight: commodityIndex === (allCommodities.length || 1) - 1 ? "1px solid rgba(0,0,0,0.08)" : "none",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row && commodity ? "text.primary" : "text.disabled" }}>
                                {row && commodity
                                  ? formatSummaryValue(row.grades?.[commodity.key])
                                  : "—"}
                              </Typography>
                            </Box>
                          )
                        )}
                        {(allCommodities.length ? allCommodities : [null]).map(
                          (commodity, commodityIndex) => (
                            <Box
                              component="td"
                              key={`resource-contained-${resource.id}-${index}-${commodity?.key || "empty"}`}
                              sx={{
                                px: 0.5,
                                py: 0.5,
                                textAlign: "center",
                                borderRight: commodityIndex === (allCommodities.length || 1) - 1 ? "1px solid rgba(0,0,0,0.08)" : "none",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row && commodity ? "text.primary" : "text.disabled" }}>
                                {row && commodity
                                  ? formatSummaryValue(row.contained?.[commodity.key])
                                  : "—"}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Box>
                    ));

                    const economicScenarios = resource.latest_resource_report?.economic_scenarios || [];
                    const scenarioRows = economicScenarios.length
                      ? [
                          <Box
                            component="tr"
                            key={`resource-scenarios-${resource.id}`}
                            sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", boxShadow: "inset 4px 0 0 rgba(15, 23, 42, 0.25)" }}
                          >
                            <Box component="td" sx={{ px: 0.5, py: 1 }} />
                            <Box component="td" colSpan={detailColumnCount - 1} sx={{ px: 1, py: 1 }}>
                              <Box
                                sx={{
                                  border: "1px solid rgba(0,0,0,0.08)",
                                  borderRadius: 1,
                                  p: 1,
                                  backgroundColor: "rgba(34, 35, 37, 0.02)",
                                  
                                }}
                              >
                                <Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: 0.5 }}>
                                  Economic Scenarios (NPV)
                                </Typography>
                                <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                                  <Box component="thead">
                                    <Box component="tr" sx={{ backgroundColor: "rgba(34, 35, 37, 0.04)" }}>
                                      <Box component="th" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          Name
                                        </Typography>
                                      </Box>
                                      <Box component="th" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          Currency
                                        </Typography>
                                      </Box>
                                      <Box component="th" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          Tax
                                        </Typography>
                                      </Box>
                                      <Box component="th" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          Discount
                                        </Typography>
                                      </Box>
                                      <Box component="th" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          Price Assumptions
                                        </Typography>
                                      </Box>
                                      <Box component="th" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          NPV
                                        </Typography>
                                      </Box>
                                      <Box component="th" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          IRR
                                        </Typography>
                                      </Box>
                                      <Box component="th" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                        <Typography variant="caption" fontWeight={600}>
                                          Payback<br />Period
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                  <Box component="tbody">
                                    {economicScenarios.map((scenario) => (
                                      <Box component="tr" key={scenario.id || scenario.label} sx={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                                        <Box component="td" sx={{ px: 0.5, py: 0.5 }}>
                                          <Typography variant="body2" sx={{ fontSize: "0.75rem", textAlign: "center" }}>
                                            {scenario.label || "—"}
                                          </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                            {scenario.currency || "—"}
                                          </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                            {formatTaxTreatment(scenario.tax_treatment)}
                                          </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                            {formatDiscountRate(scenario.discount_rate)}
                                          </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 0.5, py: 0., textAlign: "center" }}>
                                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                            {formatPriceAssumptions(scenario.prices)}
                                          </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                            {formatSummaryValue(scenario.npv_value)}
                                          </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                            {formatIrrValue(scenario.irr_value)}
                                          </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center" }}>
                                          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                            {formatPaybackPeriod(scenario.payback_period)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </Box>,
                        ]
                      : [];

                    return [...resourceRowEntries, ...scenarioRows];
                  });

                  return [...assetRowsRendered, ...resourceRowsRendered];
                })}
                {(companyRows.length ? companyRows : [null]).map((row, index) => (
                  <Box
                    component="tr"
                    key={`company-total-${index}`}
                    sx={{
                      borderTop: index === 0 ? "2px solid rgba(0,0,0,0.7)" : "none",
                      backgroundColor: "rgba(34, 35, 37, 0.08)",
                      boxShadow: "inset 4px 0 0 rgba(15, 23, 42, 0.6)",
                    }}
                  >
                    <Box
                      component="td"
                      sx={{
                        px: 0.5,
                        py: 0.5,
                        verticalAlign: "top",
                        width: { xs: 125, sm: 300 },
                        minWidth: { xs: 125, sm: 300 },
                        maxWidth: { xs: 125, sm: 300 },
                        borderRight: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      {index === 0 ? (
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.75rem", pl:2, pt:1 }}>
                          Company Totals
                        </Typography>
                      ) : null}
                    </Box>
                    <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center", borderRight: "1px solid rgba(0,0,0,0.08)" }}
                      >
                      {row ? (
                        <Typography variant="caption" fontWeight={600}>
                          {row.classification.label}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.75rem" }}> {"\u2014"} </Typography>
                      )}
                    </Box>
                    <Box component="td" sx={{ px: 0.5, py: 0.5, textAlign: "center", borderRight: "1px solid rgba(0,0,0,0.08)" }}
                      >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row ? "text.primary" : "text.disabled" }}>
                        {row ? formatSummaryValue(row.tonnage) : "\u2014"}
                      </Typography>
                    </Box>
                    {(allCommodities.length ? allCommodities : [null]).map((commodity, commodityIndex) => (
                      <Box
                        component="td"
                        key={`company-grade-${index}-${commodity?.key || "empty"}`}
                        sx={{
                          px: 0.5,
                          py: 0.5,
                          textAlign: "center",
                          borderRight: commodityIndex === (allCommodities.length || 1) - 1 ? "1px solid rgba(0,0,0,0.08)" : "none",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row && commodity ? "text.primary" : "text.disabled" }}>
                          {row && commodity ? formatSummaryValue(row.grades?.[commodity.key]) : "\u2014"}
                        </Typography>
                      </Box>
                    ))}
                    {(allCommodities.length ? allCommodities : [null]).map((commodity) => (
                      <Box
                        component="td"
                        key={`company-contained-${index}-${commodity?.key || "empty"}`}
                        sx={{
                          px: 0.5,
                          py: 0.5,
                          textAlign: "center"
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "0.75rem", color: row && commodity ? "text.primary" : "text.disabled" }}>
                          {row && commodity ? formatSummaryValue(row.contained?.[commodity.key]) : "\u2014"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Stack>
      )}
      

      <AssetResourceDetailDrawer
        open={detailDrawer.open}
        onClose={closeDetailDrawer}
        type={detailDrawer.type}
        data={detailDrawer.data}
      />
    </Box>
  );
}
